import { createEffect, createMemo, createResource, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import type { CoworkArtifact } from "@/cowork/api"
import { ReportPreview } from "@/pages/session/v2/cowork-files-panel"
import { ReportHtmlPreview } from "./report-html-preview"
import { printableReport } from "@/cowork/report-print"
import { reportDisplayTitle } from "@/cowork/report-preview"

export function PublicReportPreview(props: {
  title: string
  format: string
  filename: string
  createdAt: string
  load: () => Promise<{ blob: Blob; filename?: string }>
}) {
  const language = useLanguage()
  const format = props.format.toLowerCase().replace(/^\./, "")
  const office = /^(xlsx|xlsm|excel|spreadsheet|workbook|docx|word|pptx|slides|presentation|powerpoint)$/.test(format)
  const text = /^(json|xml|csv|txt|text|md|markdown)$/.test(format)
  const html = /^(html|htm|text\/html)$/.test(format)
  const [file, fileActions] = createResource(async () => {
    const result = await props.load()
    return { ...result, text: text || html ? await result.blob.text() : undefined }
  })
  const [state, setState] = createStore({ url: "", printReady: false, printError: false, printHint: false })
  let printFrame: HTMLIFrameElement | undefined
  const printDocument = createMemo(() => !file.error && file()?.text !== undefined ? printableReport(props.title, format, file()!.text!) : undefined)
  createEffect(() => { printDocument(); setState({ printReady: false, printError: false, printHint: false }) })
  const print = () => {
    if (!state.printReady || !printFrame?.contentWindow) return
    try {
      printFrame.contentWindow.focus()
      printFrame.contentWindow.print()
      setState({ printHint: true, printError: false })
    } catch { setState("printError", true) }
  }
  createEffect(() => {
    if (file.error) return
    const value = file()
    if (!value) return
    const url = URL.createObjectURL(value.blob)
    setState("url", url)
    onCleanup(() => URL.revokeObjectURL(url))
  })
  // Public links supply their own download without exposing private report metadata.
  const artifact: CoworkArtifact = {
    id: "public-report", company_id: "", project_id: "", artifact_type: "report", format,
    title: props.title, artifact_metadata: {}, report_status: "draft", validation_status: "incomplete",
    entity_scope: {}, source_coverage: {}, summary: {}, exceptions: [], validation_results: [], lineage: [],
    mapping_coverage_details: {}, status: "created", created_at: props.createdAt, updated_at: props.createdAt,
  }
  return (
    <section class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base" aria-label={props.title}>
      <Show when={!file.loading} fallback={
        <div role="status" class="grid min-h-60 flex-1 place-items-center text-13-regular text-v2-text-text-muted">
          {language.t("cowork.files.panel.loadingPreview")}
        </div>
      }>
        <Show when={!file.error} fallback={<div role="alert" class="p-6 text-13-regular"><p>{language.t("cowork.report.share.downloadError")}</p><button class="report-workspace-action mt-3" onClick={() => void Promise.resolve(fileActions.refetch()).catch(() => undefined)}>{language.t("cowork.enterprise.scope.retry")}</button></div>}>
          <Show when={file()} keyed>{(value) => (
            <>
              <div class="report-preview-toolbar shrink-0">
                <span class="min-w-0 truncate text-12-regular text-v2-text-text-muted">{reportDisplayTitle(props.filename)}</span>
                <div class="flex flex-wrap items-center gap-2">
                <Show when={printDocument() !== undefined}><button type="button" class="report-workspace-action" disabled={!state.printReady} onClick={print}>{language.t("cowork.report.workspace.print")}</button></Show>
                <Show when={format === "pdf"}><a class="report-workspace-action" href={state.url} download={value.filename || props.filename}>{language.t("cowork.enterprise.reports.exportPdf")}</a></Show>
                <a class="shrink-0 rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium hover:bg-v2-background-bg-layer-02"
                  href={state.url} download={value.filename || props.filename} rel="noreferrer">
                  {language.t("cowork.enterprise.reports.download")}
                </a>
                </div>
              </div>
              <Show when={state.printHint || state.printError}><p role={state.printError ? "alert" : "status"} class="px-4 py-2 text-12-regular">{language.t(state.printError ? "cowork.report.workspace.printError" : "cowork.report.workspace.printHint")}</p></Show>
              <Show when={printDocument()}>{(document) => <iframe ref={printFrame} title={language.t("cowork.report.workspace.print")}
                class="fixed -left-[10000px] top-0 h-[1000px] w-[1024px]" aria-hidden="true" tabindex={-1}
                srcdoc={document()} sandbox="allow-same-origin allow-modals" referrerpolicy="no-referrer" onLoad={() => setState("printReady", true)} />}</Show>
              <div class="min-h-0 flex-1 overflow-auto">
                <Show when={office || format === "csv"} fallback={
                  <Show when={html} fallback={<Show when={text} fallback={
                    <iframe class="h-full min-h-[560px] w-full bg-white" src={state.url}
                      sandbox="allow-same-origin" referrerPolicy="no-referrer" title={props.title} />
                  }>
                    <pre class="whitespace-pre-wrap p-5 text-13-regular">{value.text}</pre>
                  </Show>}>
                    <ReportHtmlPreview srcdoc={value.text} title={props.title} />
                  </Show>
                }>
                  <ReportPreview artifact={artifact} load={async () => value} />
                </Show>
              </div>
            </>
          )}</Show>
        </Show>
      </Show>
    </section>
  )
}
